import React, { useState } from 'react';
import { useI18n } from '../../context/i18n';
import { playsTranslations } from './playsCopy';
import { activatePlay, PlaysApiError } from './playsApi';
import './LaunchPlayButton.css';

// Activation entry point for a single Play (draft -> active). Shows
// consumption BEFORE confirmation per spec section 2 critical UX requirement:
// the launch button always reads "Launch as One-time Play. Uses 1 of your
// {n} monthly Plays." (or "Unlimited monthly Plays" on Pro/founding), taken
// from entitlements.launch_button_copy so the number always matches the
// account's real remaining balance, not a locally computed guess.
//
// Clicking opens a two-option modal (Launch once / Keep updated
// automatically). On free-tier or capacity errors the backend returns a
// structured {error, code, meta} the modal renders as pack/upgrade prompts,
// never a silent block.
export default function LaunchPlayButton({
  token,
  playId,
  entitlements,
  canActivate,
  onActivated,
  onNavigateToPricing,
  className = '',
}) {
  const { language } = useI18n();
  const copy = playsTranslations[language]?.activation || playsTranslations.en.activation;
  const pricingCopy = playsTranslations[language]?.pricing || playsTranslations.en.pricing;

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(null); // 'launch_once' | 'keep_updated'
  const [provider, setProvider] = useState(null); // csv | connected provider
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorState, setErrorState] = useState(null); // { message, code, meta }

  const launchButtonCopy = entitlements?.launch_button_copy
    || 'Launch as One-time Play. Uses 1 of your monthly Plays.';

  const openModal = () => {
    setErrorState(null);
    setMode(null);
    setProvider(null);
    setIsOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    setErrorState(null);
  };

  const confirm = async () => {
    if (!mode || !provider) return;
    setIsSubmitting(true);
    setErrorState(null);
    try {
      const result = await activatePlay({
        token, playId, mode,
        deliveryType: provider === 'csv' ? 'export' : 'integration',
        provider,
      });
      setIsOpen(false);
      onActivated?.(result);
    } catch (err) {
      if (err instanceof PlaysApiError) {
        setErrorState({ message: err.message, code: err.code, meta: err.meta });
      } else {
        setErrorState({ message: err?.message || copy.genericError });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Free tier: button stays visible (never hidden) but gated with the exact
  // free-gate copy instead of opening the activation modal.
  if (canActivate === false) {
    return (
      <div className={`launch-play ${className}`}>
        <button type="button" className="launch-play-btn launch-play-btn-gated" onClick={openModal}>
          {launchButtonCopy}
        </button>
        {isOpen && (
          <div className="launch-play-overlay" role="dialog" aria-modal="true">
            <div className="launch-play-modal">
              <p className="launch-play-gate-message">{copy.freeBlocked}</p>
              <div className="launch-play-actions">
                <button type="button" className="launch-play-secondary" onClick={closeModal}>
                  {copy.cancel}
                </button>
                {onNavigateToPricing && (
                  <button type="button" className="launch-play-primary" onClick={onNavigateToPricing}>
                    {pricingCopy.selectPlan}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`launch-play ${className}`}>
      <button type="button" className="launch-play-btn" onClick={openModal}>
        {launchButtonCopy}
      </button>

      {isOpen && (
        <div className="launch-play-overlay" role="dialog" aria-modal="true">
          <div className="launch-play-modal">
            <h4 className="launch-play-title">{copy.title}</h4>

            <div className="launch-play-options">
              <button
                type="button"
                className={`launch-play-option ${provider === 'csv' ? 'is-selected' : ''}`}
                onClick={() => { setProvider('csv'); setMode('launch_once'); }}
              >
                <span className="launch-play-option-name">Export CSV</span>
                <span className="launch-play-option-desc">Uses one monthly Play. No campaign is created.</span>
              </button>
              {(entitlements?.ready_delivery_providers || []).map((connectedProvider) => (
                <button
                  key={connectedProvider}
                  type="button"
                  className={`launch-play-option ${provider === connectedProvider ? 'is-selected' : ''}`}
                  onClick={() => { setProvider(connectedProvider); setMode(null); }}
                >
                  <span className="launch-play-option-name">Activate in {connectedProvider === 'klaviyo' ? 'Klaviyo' : connectedProvider === 'omnisend' ? 'Omnisend' : connectedProvider}</span>
                  <span className="launch-play-option-desc">Sync the audience and create a campaign Draft.</span>
                </button>
              ))}
            </div>

            {provider && provider !== 'csv' && <div className="launch-play-options">
              <button
                type="button"
                className={`launch-play-option ${mode === 'launch_once' ? 'is-selected' : ''}`}
                onClick={() => setMode('launch_once')}
              >
                <span className="launch-play-option-name">{copy.launchOnce}</span>
                <span className="launch-play-option-desc">{copy.launchOnceDesc}</span>
              </button>
              <button
                type="button"
                className={`launch-play-option ${mode === 'keep_updated' ? 'is-selected' : ''}`}
                onClick={() => setMode('keep_updated')}
              >
                <span className="launch-play-option-name">{copy.keepUpdated}</span>
                <span className="launch-play-option-desc">{copy.keepUpdatedDesc}</span>
              </button>
            </div>}

            {errorState && (
              <div className="launch-play-error" role="alert">
                <p>{errorState.message}</p>
                {(errorState.code === 'always_on_limit_reached' || errorState.code === 'one_time_limit_reached') && (
                  <div className="launch-play-error-actions">
                    {onNavigateToPricing && (
                      <button type="button" className="launch-play-error-cta" onClick={onNavigateToPricing}>
                        {copy.addPack} / {copy.upgrade}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="launch-play-actions">
              <button type="button" className="launch-play-secondary" onClick={closeModal} disabled={isSubmitting}>
                {copy.cancel}
              </button>
              <button
                type="button"
                className="launch-play-primary"
                onClick={confirm}
                disabled={!mode || !provider || isSubmitting}
              >
                {isSubmitting ? copy.launching : copy.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
